<template>
    <div class="form-floating position-relative select-offices-wrapper">
        <div class="animate input-loader" v-if="loading"></div>
        <v-select
            v-else
            :disabled="disabled"
            class="form-control"
            v-model="selectData"
            :options="data"
            :filterable="true"
            :filter-by="localFilter"
            :get-option-label="(option) => option.nombre"
            :reduce="option => option"
            @search="onSearch"
            @option:selected="setData"
        >
            <template #option="{ codigo, nombre, usuario }">
                <b>{{ codigo?.toUpperCase() }}</b> - {{ nombre }}
                <span v-if="$auth.user.rol == 'master'" class="text-muted ms-1">({{ usuario }})</span>
            </template>

            <template #selected-option="{ codigo, nombre }">
                <span class="office-selected"><b>{{ codigo?.toUpperCase() }}</b> - {{ nombre }}</span>
            </template>

            <template v-slot:no-options>
                Oficina no encontrada...
            </template>
        </v-select>
        <label>{{ titulo ? titulo : 'Selecciona Oficina' }} <span v-if="required" class="text-danger fw-bold">[*]</span></label>
        <i v-if="reload" class="bi bi-arrow-clockwise icon-refresh" @click="loadData" title="Recargar data"></i>
    </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue';
import axios from 'axios';

const props = defineProps({
    required: null,
    reload: null,
    titulo: null,
    select: null,
    id_usuario: null,
    disabled: { type: Boolean, default: false }
})

const emit = defineEmits(['selected'])

const loading = ref(false)
const data = ref([])
const selectData = ref(null)
let searchTimer = null

const setData = () => {
    if (!selectData.value) return
    emit('selected', selectData.value)
}

// Filtro local de v-select — busca en nombre, código, ciudad, estado, dirección, teléfonos
const localFilter = (option, label, search) => {
    const term = search.toLowerCase()
    return (
        option.nombre?.toLowerCase().includes(term) ||
        option.codigo?.toLowerCase().includes(term) ||
        option.ciudad?.toLowerCase().includes(term) ||
        option.estado?.toLowerCase().includes(term) ||
        option.direccion?.toLowerCase().includes(term) ||
        option.telefonos?.toLowerCase().includes(term)
    )
}

// Carga inicial — igual que antes, últimos registros
const loadData = async () => {
    loading.value = true
    try {
        const response = (await axios.get('oficinas')).data
        let results = response?.data ?? []
        if (props.id_usuario) {
            results = results.filter(item => item.id_usuario == props.id_usuario)
        }
        data.value = results
        if (props.select) {
            const found = results.find(item => item.id == props.select)
            if (found) {
                selectData.value = found
            } else {
                // No está en los resultados iniciales, buscarlo por id
                await loadById(props.select)
            }
            setData()
        }
    } catch (e) {
        data.value = []
    } finally {
        loading.value = false
    }
}

// Carga una oficina específica por id (para pre-selección en edición)
const loadById = async (id) => {
    try {
        const response = (await axios.get(`oficinas?search=id&q=${id}`)).data
        const result = response?.data?.[0] ?? null
        if (result) {
            // Añadirla a la lista si no está ya
            if (!data.value.find(item => item.id == result.id)) {
                data.value = [result, ...data.value]
            }
            selectData.value = result
        }
    } catch (e) {
        // silencioso
    }
}

// Al escribir: v-select filtra localmente primero (filterable: true)
// Si no hay coincidencias locales tras 300ms, consulta el API
const onSearch = (search) => {
    clearTimeout(searchTimer)
    if (search.length < 2) return

    searchTimer = setTimeout(async () => {
        // Verificar si ya hay coincidencias locales con lo escrito
        const term = search.toLowerCase()
        const localMatch = data.value.filter(item =>
            item.nombre?.toLowerCase().includes(term) ||
            item.codigo?.toLowerCase().includes(term) ||
            item.ciudad?.toLowerCase().includes(term) ||
            item.estado?.toLowerCase().includes(term) ||
            item.direccion?.toLowerCase().includes(term) ||
            item.telefonos?.toLowerCase().includes(term)
        )
        if (localMatch.length > 0) return // hay resultados locales, no buscar al API

        // No hay coincidencias locales — buscar en el API
        try {
            const url = `oficinas?search=q&q=${encodeURIComponent(search)}&limit=20`
            const response = (await axios.get(url)).data
            let results = response?.data ?? []
            if (props.id_usuario) {
                results = results.filter(item => item.id_usuario == props.id_usuario)
            }
            // Fusionar resultados remotos con los locales sin duplicados
            const merged = [...data.value]
            for (const r of results) {
                if (!merged.find(item => item.id == r.id)) {
                    merged.push(r)
                }
            }
            data.value = merged
        } catch (e) {
            // silencioso
        }
    }, 350)
}

onMounted(() => {
    loadData()
})

watch(() => props.select, async (newId) => {
    if (!newId) {
        selectData.value = null
        return
    }
    const existing = data.value.find(item => item.id == newId)
    if (existing) {
        selectData.value = existing
        return
    }
    await loadById(newId)
})

watch(() => props.id_usuario, () => {
    loadData()
})
</script>

<style scoped>
.icon-refresh {
    cursor: pointer;
    position: absolute;
    top: 9px;
    right: 3rem;
    padding: 1.2rem;
    font-weight: bold;
    color: var(--primary)
}

.office-selected {
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    line-height: 1.2;
}
</style>

<style>
/* Acotado al wrapper de este componente */
.select-offices-wrapper .vs__selected {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: calc(100% - 40px);
    display: block;
}

.select-offices-wrapper .vs__selected-options {
    flex-wrap: nowrap;
    overflow: hidden;
    min-width: 0;
}
</style>
